/* jshint esversion: 6 */
import React from 'react';
import PropTypes from 'prop-types';
import Content from 'app/components/common/Content.jsx';
import ReactFileReader from 'react-file-reader';
import importerModule from 'app/mixins/ImporterModule.js';
import ImportResults from 'app/components/Importer/ImportResults.jsx';
import Loading from 'app/components/common/Loading.jsx';
import FeatureUpgradeComponent from 'app/components/Subscription/FeatureUpgradeComponent.jsx';
import ComponentWithContext from 'app/components/Routers/ComponentWithContext.jsx';
import { compose } from 'redux';

class CSVImporter extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            uploadError: null,
            importError: null,
            selectedSocials: [],
            importComplete: false,
            emptyUpload: false,
            pollId: 0
        };

        // bind class functions here
        this.onFileUpload = this.onFileUpload.bind(this);
        this.startPolling = this.startPolling.bind(this);

    }

    static getDerivedStateFromProps(props, state) {
        let nextState = {},
            uploadError = [],
            importError = [];

        // a check on the upload that fails is as much a failed upload as the upload failing, and a
        // request that fails outright has no errors of its own, but has still failed
        uploadError = props.getApiErrors(['BULK_UPLOAD', 'BULK_STATUS']);
        if (uploadError.length && state.uploadError === null) {
            nextState.uploadError = uploadError[0].errors || {};
        } else if (!uploadError.length && state.uploadError !== null) {
            nextState.uploadError = null;
        }

        importError = props.getApiErrors(['BULK_POST']);
        if (importError.length && state.importError === null) {
            nextState.importError = importError[0].errors || {};
        } else if (!importError.length && state.importError !== null) {
            nextState.importError = null;
        }

        return (Object.keys(nextState).length) ? nextState : null;
    }

    componentDidMount() {

        if (this.props.socials.list.length === 0) {
             this.props.getSocials();
        }

        // clear out any prior results from say, a twitter import
        this.props.resetBulk();

        // an upload still being worked through when the page was left is picked up again
        if (this.props.bulk.token !== 0) {
            this.startPolling();
        }

    }

    componentDidUpdate(prevProps, prevState) {

        if (prevProps.bulk.token === 0 && this.props.bulk.token !== 0) {
            this.startPolling();
        }

        if (prevProps.bulk.token !== 0 && this.props.bulk.token === 0) {
            clearInterval(this.state.pollId);
            this.setState({
                pollId: 0,
                emptyUpload: this.props.bulk.searchResults.length === 0
            });
        }

        if (prevProps.bulk.completedImports < this.props.bulk.completedImports) {
            this.setState({
                selectedSocials: [],
                importComplete: true
            });
        }

    }

    componentWillUnmount() {

        clearInterval(this.state.pollId);

    }

    onFileUpload(files) {

        this.props.uploadBulkItems(files);
        this.props.deleteApiError(['BULK_UPLOAD', 'BULK_STATUS', 'BULK_POST']);

        this.setState({
            importError: null,
            emptyUpload: false
        });

    }

    startPolling() {

        let pollId = setInterval(() => {
            this.props.getBulkStatus(this.props.bulk.token);
        }, 1500);

        this.setState({
            pollId
        });

    }

    render() {
        let content = null,
            results = null,
            constants = this.props.app.config.constants,
            errors = [],
            twitterOutputs = this.props.socials.list.filter((social) => {
                return constants.POST_SVC_TWITTER == social.psID;
            }).map((social) => {
                return social.id;
            }),
            couldNotImport = (serverSaid) => {
                return (
                    <div className="import-loader error">
                        Sorry, we couldn't import your CSV for you.{serverSaid ? ` The server said: ${serverSaid}` : ''}
                    </div>
                );
            };

        if (!this.props.user.perms.resources.bulkImport) {
            content = (
                <FeatureUpgradeComponent
                    {...this.props}
                    context="contentImport"/>
            );
            return (
                <Content main={content}/>
            );
        }

        if ((this.props.app.loading.BULK_RESULTS || this.props.app.loading.BULK_UPLOAD) || this.props.bulk.token !== 0) {
            let progress = "...";
            if (this.props.bulk.status !== null) {
                progress = " " + (parseFloat(this.props.bulk.status) * 100).toFixed(0) + "%";
            }
            results = (
                <div className="import-loader empty">
                    <Loading label={`Importing${progress}`}/>
                </div>
            );
        } else if (this.props.bulk.searchResults.length > 0 || this.state.uploadError !== null) {
            if (this.state.uploadError) {
                results = couldNotImport(this.state.uploadError.validation?.media);
            } else {
                results = (
                    <ImportResults
                        {...this.props}
                        context='csv'
                        items={this.props.bulk.searchResults}
                        service={0}
                        chooseSocials={this.onChooseSocials}
                        selectedSocials={this.state.selectedSocials}
                        onImportItems={this.onImportItems}
                        importComplete={this.state.importComplete}
                        categories={this.props.categories}
                        importError={this.state.importError}
                        onSocialsSelected={this.onSocialsSelected}
                        socials={this.props.socials.list}
                        twitterOutputs={twitterOutputs} />
                );
            }
        } else if (this.state.emptyUpload) {
            // the upload was worked through, but none of its rows came back
            results = couldNotImport();
        } else {
            results = (
                <div className="import-results-wrapper empty"></div>
            );
        }

        content = (
            <div className="importer-main">
                <div className="import-search-block">
                    <h2>Import a CSV</h2>
                    <div className="csv-importer">
                        <div className="format-instructions">
                            <p>
                                You can upload a CSV of your content and add items directly to your social queues or EverQueues.
                                We expect the data in the following format (if you include the header, be sure it's the first row):
                            </p>
                            <pre>
                                comment, url, imageUrl
                            </pre>
                        </div>
                        <ReactFileReader handleFiles={this.onFileUpload} fileTypes='text/csv'>
                            <button className="btn-primary active csv-button">Upload CSV</button>
                        </ReactFileReader>
                    </div>
                </div>
                {results}
            </div>
        );

        return (
            <Content header={null} main={content}/>
        );
    }
}

CSVImporter.displayName = "CSVImporter";

const enhance = compose(
    ComponentWithContext,
    importerModule
);

export default enhance(CSVImporter);
